import { isAxiosError } from 'axios';
import { z } from 'zod';
import type { AxiosRequestConfig } from 'axios';
import axios from '@/plugins/axios.js';
import BaseApi from '@/api/BaseApi.ts';
import {
    AiDraftErrorCode,
    AiDraftFailureReason,
    CancelDraftRequestSchema,
    CancelDraftResponseSchema,
    DraftResponseStatus,
    GenerateEmailDraftRequestSchema,
    GenerateEmailDraftResponseSchema,
    GenerateSmsDraftRequestSchema,
    GenerateSmsDraftResponseSchema,
    UpdateEmailDraftRequestSchema,
    UpdateEmailDraftResponseSchema,
    UpdateSmsDraftRequestSchema,
    UpdateSmsDraftResponseSchema,
    type CancelDraftRequest,
    type GenerateEmailAiDraftResult,
    type GenerateEmailDraftRequest,
    type GenerateSmsDraftRequest,
    type GenerateSmsDraftResponse,
    type SmsAiDraftCommonResult,
    type SubmitDraftFeedbackPayload,
    type UpdateEmailAiDraftResult,
    type UpdateEmailDraftRequest,
    type UpdateSmsAiDraftResult,
    type UpdateSmsDraftRequest,
    type UpdateSmsDraftResponse,
} from '@/api/aiDraft/types.ts';

const DEFAULT_RATE_LIMIT_SECONDS = 60;
const RETRY_AFTER_HEADER = 'Retry-After';

type NonRetriedRequestConfig = AxiosRequestConfig & {
    'axios-retry': {
        retries: 0;
    };
};

const createNonRetriedRequestConfig = (): NonRetriedRequestConfig => ({
    'axios-retry': {
        retries: 0,
    },
});

const AiDraftErrorBodySchema = z.object({
    error: z
        .object({
            code: z.string().optional(),
        })
        .optional(),
});

const RateLimitBodySchema = z.object({
    error: z
        .object({
            retry_after: z.number().int().nonnegative().optional(),
        })
        .optional(),
    data: z
        .object({
            error: z
                .object({
                    retry_after: z.number().int().nonnegative().optional(),
                })
                .optional(),
        })
        .optional(),
});

type AiDraftErrorContext = {
    status: number;
    data: unknown;
    headers: unknown;
    code: string | null;
    hasBody: boolean;
};

type AiDraftRateLimitError = Error & { rateLimitSeconds: number };
type GenerateEmailDraftResponseData = z.infer<typeof GenerateEmailDraftResponseSchema>['data'];
type UpdateEmailDraftResponseData = z.infer<typeof UpdateEmailDraftResponseSchema>['data'];

const createAiDraftRateLimitError = (rateLimitSeconds: number): AiDraftRateLimitError => {
    const error = new Error('AI draft rate limit exceeded') as AiDraftRateLimitError;
    error.name = 'AiDraftRateLimitError';
    error.rateLimitSeconds = rateLimitSeconds;

    return error;
};

const parseRetryAfterValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        return Math.trunc(value);
    }

    if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10);

        return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
    }

    return null;
};

const getAiDraftErrorContext = (error: unknown): AiDraftErrorContext | null => {
    if (!isAxiosError(error) || !error.response) {
        return null;
    }

    const data = error.response.data;
    const parseResult = AiDraftErrorBodySchema.safeParse(data);
    const errorBody = parseResult.success ? parseResult.data : null;

    return {
        status: error.response.status,
        data,
        headers: error.response.headers,
        code: errorBody?.error?.code ?? null,
        hasBody: data !== undefined && data !== null,
    };
};

const getHeader = (headers: unknown, headerName: string): unknown => {
    if (!headers || typeof headers !== 'object') {
        return null;
    }

    const headerGetter = (headers as { get?: (name: string) => unknown }).get;

    if (typeof headerGetter === 'function') {
        return (
            headerGetter.call(headers, headerName) ??
            headerGetter.call(headers, headerName.toLowerCase())
        );
    }

    const normalizedHeaderName = headerName.toLowerCase();

    return Object.entries(headers as Record<string, unknown>).find(
        ([key]) => key.toLowerCase() === normalizedHeaderName
    )?.[1];
};

const getRetryAfterFromBody = (data: unknown): number | null => {
    const parseResult = RateLimitBodySchema.safeParse(data);

    if (!parseResult.success) {
        return null;
    }

    return parseRetryAfterValue(
        parseResult.data.error?.retry_after ?? parseResult.data.data?.error?.retry_after
    );
};

const getRateLimitSeconds = (context: AiDraftErrorContext): number => {
    return (
        getRetryAfterFromBody(context.data) ??
        parseRetryAfterValue(getHeader(context.headers, RETRY_AFTER_HEADER)) ??
        DEFAULT_RATE_LIMIT_SECONDS
    );
};

const mapGenerateEmailDraftResult = (
    data: GenerateEmailDraftResponseData
): GenerateEmailAiDraftResult => {
    if (data.status === 'cancelled') {
        return {
            status: 'cancelled',
            interactionId: data.interaction_id,
            traceId: data.trace_id,
        };
    }

    return {
        subject: data.draft.subject,
        body: data.draft.body,
        interactionId: data.interaction_id,
        traceId: data.trace_id,
    };
};

const mapUpdateEmailDraftResult = (
    data: UpdateEmailDraftResponseData
): UpdateEmailAiDraftResult => {
    if (data.status === DraftResponseStatus.Unsupported) {
        return {
            status: 'unsupported',
            interactionId: data.interaction_id,
            traceId: data.trace_id,
            reason: data.reason,
        };
    }

    return mapGenerateEmailDraftResult(data);
};

const buildRateLimitResult = (context: AiDraftErrorContext) => ({
    ok: false as const,
    reason: AiDraftFailureReason.RateLimited,
    rateLimitSeconds: getRateLimitSeconds(context),
});

// Email drafts surface rate limits by throwing; every other error rethrows unchanged.
const rethrowEmailDraftRateLimit = (error: unknown): never => {
    const errorContext = getAiDraftErrorContext(error);

    if (errorContext?.status === 429) {
        throw createAiDraftRateLimitError(getRateLimitSeconds(errorContext));
    }

    throw error;
};

type SmsDraftStaticFailureReason = Exclude<
    AiDraftFailureReason,
    | typeof AiDraftFailureReason.RateLimited
    | typeof AiDraftFailureReason.Cancelled
    | typeof AiDraftFailureReason.Unsupported
>;

type SmsDraftStaticFailureMapping = {
    code: AiDraftErrorCode;
    reason: SmsDraftStaticFailureReason;
};

const SMS_DRAFT_STATIC_FAILURE_BY_STATUS: Partial<Record<number, SmsDraftStaticFailureMapping>> = {
    403: {
        code: AiDraftErrorCode.NotEntitled,
        reason: AiDraftFailureReason.NotEntitled,
    },
    422: {
        code: AiDraftErrorCode.ServiceLeadNotSupported,
        reason: AiDraftFailureReason.ServiceLead,
    },
};

const getSmsDraftStaticFailureReason = (
    context: AiDraftErrorContext
): SmsDraftStaticFailureReason | null => {
    const mapping = SMS_DRAFT_STATIC_FAILURE_BY_STATUS[context.status];

    if (!mapping || (context.hasBody && context.code !== mapping.code)) {
        return null;
    }

    return mapping.reason;
};

const buildSmsDraftFailureResult = (
    context: AiDraftErrorContext | null
): SmsAiDraftCommonResult | null => {
    if (!context) {
        return null;
    }

    if (context.status === 429) {
        return buildRateLimitResult(context);
    }

    const reason = getSmsDraftStaticFailureReason(context);

    return reason ? { ok: false, reason } : null;
};

type CancellableDraftData = GenerateSmsDraftResponse['data'] | UpdateSmsDraftResponse['data'];

type CancelledDraftData = Extract<
    CancellableDraftData,
    { status: typeof DraftResponseStatus.Cancelled }
>;

const isCancelledDraft = (data: CancellableDraftData): data is CancelledDraftData =>
    data.status === DraftResponseStatus.Cancelled;

type UnsupportedDraftData = Extract<
    CancellableDraftData,
    { status: typeof DraftResponseStatus.Unsupported }
>;

const isUnsupportedDraft = (data: CancellableDraftData): data is UnsupportedDraftData =>
    data.status === DraftResponseStatus.Unsupported;

class AiDraftAPI extends BaseApi {
    name = 'AiDraftAPI';

    async generateEmailDraft(
        leadId: number,
        request: GenerateEmailDraftRequest
    ): Promise<GenerateEmailAiDraftResult> {
        const requestPayload = this.validateGenerateRequest(request);

        try {
            const response = await axios.post(
                `v1/leads/${leadId}/ai/draft/email`,
                {
                    locale: requestPayload.locale,
                    request_id: requestPayload.requestId,
                    existing_subject: requestPayload.existingSubject ?? null,
                },
                createNonRetriedRequestConfig()
            );
            const validatedResponse = this._validateResponse(
                GenerateEmailDraftResponseSchema,
                response.data
            );
            return mapGenerateEmailDraftResult(validatedResponse.data);
        } catch (error) {
            return rethrowEmailDraftRateLimit(error);
        }
    }

    async updateEmailDraft(
        leadId: number,
        request: UpdateEmailDraftRequest
    ): Promise<UpdateEmailAiDraftResult> {
        const requestPayload = this.validateUpdateRequest(request);

        try {
            const response = await axios.post(
                `v1/leads/${leadId}/ai/draft/email/update`,
                {
                    locale: requestPayload.locale,
                    request_id: requestPayload.requestId,
                    subject: requestPayload.subject,
                    body: requestPayload.body,
                    instructions: requestPayload.instructions ?? null,
                },
                createNonRetriedRequestConfig()
            );
            const validatedResponse = this._validateResponse(
                UpdateEmailDraftResponseSchema,
                response.data
            );
            return mapUpdateEmailDraftResult(validatedResponse.data);
        } catch (error) {
            return rethrowEmailDraftRateLimit(error);
        }
    }

    async generateSmsDraft(
        leadId: number,
        request: GenerateSmsDraftRequest
    ): Promise<SmsAiDraftCommonResult> {
        const requestPayload = this.validateGenerateSmsRequest(request);

        try {
            const response = await axios.post(`v1/leads/${leadId}/ai/draft/sms`, {
                locale: requestPayload.locale,
                request_id: requestPayload.requestId,
            });
            const validatedResponse = this._validateResponse(
                GenerateSmsDraftResponseSchema,
                response.data
            );
            const data = validatedResponse.data;

            if (isCancelledDraft(data)) {
                return { ok: false, reason: AiDraftFailureReason.Cancelled };
            }

            return {
                ok: true,
                text: data.draft.text,
                interactionId: data.interaction_id,
                traceId: data.trace_id,
            };
        } catch (error) {
            const failureResult = buildSmsDraftFailureResult(getAiDraftErrorContext(error));

            if (failureResult) {
                return failureResult;
            }

            throw error;
        }
    }

    async updateSmsDraft(
        leadId: number,
        request: UpdateSmsDraftRequest
    ): Promise<UpdateSmsAiDraftResult> {
        const requestPayload = this.validateUpdateSmsRequest(request);

        try {
            const response = await axios.post(`v1/leads/${leadId}/ai/draft/sms/update`, {
                locale: requestPayload.locale,
                request_id: requestPayload.requestId,
                text: requestPayload.text,
                instructions: requestPayload.instructions,
            });
            const validatedResponse = this._validateResponse(
                UpdateSmsDraftResponseSchema,
                response.data
            );
            const data = validatedResponse.data;

            if (isCancelledDraft(data)) {
                return { ok: false, reason: AiDraftFailureReason.Cancelled };
            }

            if (isUnsupportedDraft(data)) {
                return {
                    ok: false,
                    reason: AiDraftFailureReason.Unsupported,
                    unsupportedReason: data.reason,
                };
            }

            return {
                ok: true,
                text: data.draft.text,
                interactionId: data.interaction_id,
                traceId: data.trace_id,
            };
        } catch (error) {
            const failureResult = buildSmsDraftFailureResult(getAiDraftErrorContext(error));

            if (failureResult) {
                return failureResult;
            }

            throw error;
        }
    }

    async cancelDraft(leadId: number, request: CancelDraftRequest): Promise<void> {
        const requestPayload = this.validateCancelRequest(request);

        const response = await axios.post(`v1/leads/${leadId}/ai/draft/cancel`, {
            request_id: requestPayload.requestId,
            channel: requestPayload.channel,
        });
        this._validateResponse(CancelDraftResponseSchema, response.data);
    }

    async submitDraftFeedback(leadId: number, payload: SubmitDraftFeedbackPayload): Promise<void> {
        const trimmedDetails = (payload.details ?? '').trim();
        const requestPayload = {
            interaction_id: payload.interactionId,
            feedback: payload.feedback,
            reasons: payload.reasons ?? [],
            details: trimmedDetails.length > 0 ? trimmedDetails : null,
        };

        await axios.post(`v1/leads/${leadId}/ai/draft/feedback`, requestPayload);
    }

    private validateGenerateRequest(request: GenerateEmailDraftRequest): GenerateEmailDraftRequest {
        const parseResult = GenerateEmailDraftRequestSchema.safeParse(request);

        if (!parseResult.success) {
            throw new Error(`${this.name} request validation failed: ${parseResult.error.message}`);
        }

        return parseResult.data;
    }

    private validateUpdateRequest(request: UpdateEmailDraftRequest): UpdateEmailDraftRequest {
        const parseResult = UpdateEmailDraftRequestSchema.safeParse(request);

        if (!parseResult.success) {
            throw new Error(`${this.name} request validation failed: ${parseResult.error.message}`);
        }

        return parseResult.data;
    }

    private validateGenerateSmsRequest(request: GenerateSmsDraftRequest): GenerateSmsDraftRequest {
        const parseResult = GenerateSmsDraftRequestSchema.safeParse(request);

        if (!parseResult.success) {
            throw new Error(`${this.name} request validation failed: ${parseResult.error.message}`);
        }

        return parseResult.data;
    }

    private validateUpdateSmsRequest(request: UpdateSmsDraftRequest): UpdateSmsDraftRequest {
        const parseResult = UpdateSmsDraftRequestSchema.safeParse(request);

        if (!parseResult.success) {
            throw new Error(`${this.name} request validation failed: ${parseResult.error.message}`);
        }

        return parseResult.data;
    }

    private validateCancelRequest(request: CancelDraftRequest): CancelDraftRequest {
        const parseResult = CancelDraftRequestSchema.safeParse(request);

        if (!parseResult.success) {
            throw new Error(`${this.name} request validation failed: ${parseResult.error.message}`);
        }

        return parseResult.data;
    }
}

const aiDraftApi = new AiDraftAPI();

export default {
    cancelDraft: aiDraftApi.cancelDraft.bind(aiDraftApi),
    generateEmailDraft: aiDraftApi.generateEmailDraft.bind(aiDraftApi),
    updateEmailDraft: aiDraftApi.updateEmailDraft.bind(aiDraftApi),
    generateSmsDraft: aiDraftApi.generateSmsDraft.bind(aiDraftApi),
    updateSmsDraft: aiDraftApi.updateSmsDraft.bind(aiDraftApi),
    submitDraftFeedback: aiDraftApi.submitDraftFeedback.bind(aiDraftApi),
};
